import type { AnnualReturnDispensation } from 'stores/annual-returns-store';
import { resolveOrganisationName } from 'src/utils';
import { useRegionLookup } from '../useRegionLookup';

/**
 * Resolves the body that awarded a dispensation. A dispensation is granted by a
 * region (its `region_id`) or centrally by the organisation (`region_id` null,
 * e.g. UGLE / SGC). Names are resolved from the client-side regions dictionary
 * and the organisation constant by id, rather than from an object embedded on
 * the dispensation.
 */
export function useDispensationAwardedBy() {
  const { getRegion } = useRegionLookup();

  /** The awarding body's short name for the "Awarded by …" label. */
  function awardedByShortName(dispensation: AnnualReturnDispensation): string {
    const region = getRegion(dispensation.region_id);
    if (region) return region.region_short_name || region.region_name || '—';
    return resolveOrganisationName(dispensation.organisation_id) || '—';
  }

  /** The awarding body's full name, for prose (e.g. "…pay X directly"). */
  function awardedByName(dispensation: AnnualReturnDispensation): string {
    const region = getRegion(dispensation.region_id);
    if (region) {
      return (
        region.region_name || region.region_short_name || 'the awarding region'
      );
    }
    return (
      resolveOrganisationName(dispensation.organisation_id, true) ||
      'the awarding body'
    );
  }

  return { awardedByShortName, awardedByName };
}
